import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, useMediaQuery, Theme } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  Body,
  BottomActionArea,
  BottomActionButton,
  Header,
  NavigateBeforeButton,
  FullScreenLoader,
} from "@components";
import { TemplateType } from "@interfaces";
import { useMedicineSearchContext } from "@contexts";
import { useTemplateEditContext } from "../../context/TemplateEditProvider";
import { useUpdateTemplate } from "@hooks";
import { PrescriptionDetailCard } from "./components/PrescriptionDetailCard";
import {
  AddMedicineButton,
  ContentContainer,
  EmptyRequest,
  Footer,
  HeaderContainer,
  PrescriptionContent,
  PrescriptionDetailsContainer,
  RouterLink,
  SectionSubtitle,
  Title,
  StyledTextField,
} from "./styles/PrescriptionCreate.styles";
import { StyledInfoIconSecondary } from "@styles/styled";

export function TemplateEdit() {
  const navigate = useNavigate();
  const { requestId = "", templateId = "" } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { setSearchText } = useMedicineSearchContext();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const {
    templateName,
    setTemplateName,
    templateDetails,
    removeDetail,
    isLoading,
  } = useTemplateEditContext();

  const { mutateAsync: updateTemplate, isPending: isSaving } =
    useUpdateTemplate();

  const heldDetails = useMemo(
    () => templateDetails.filter((d) => d.prescriptionType === "HELD"),
    [templateDetails],
  );
  const simpleDetails = useMemo(
    () => templateDetails.filter((d) => d.prescriptionType !== "HELD"),
    [templateDetails],
  );

  useEffect(() => {
    setSearchText("");
  }, [setSearchText]);

  const handleBack = useCallback(() => {
    navigate(`/app/solicitudes/${requestId}/receta/crear/buscar`);
  }, [navigate, requestId]);

  const handleEdit = useCallback(
    (detail: { localId?: string }) => {
      if (!detail.localId) return;
      navigate(
        `/app/solicitudes/${requestId}/plantilla/${templateId}/editar/detalle/${detail.localId}`,
      );
    },
    [navigate, requestId, templateId],
  );

  const handleSave = useCallback(async () => {
    if (!templateName.trim()) {
      enqueueSnackbar("El nombre de la plantilla es requerido", {
        variant: "error",
      });
      return;
    }
    if (templateDetails.length === 0) {
      enqueueSnackbar("Agrega al menos un medicamento", {
        variant: "error",
      });
      return;
    }
    try {
      await updateTemplate({
        templateId,
        name: templateName.trim(),
        templateType: TemplateType.PRESCRIPTION,
        prescriptionDetails: templateDetails.map(
          ({ localId, ...detail }) => detail,
        ),
      });
      enqueueSnackbar("Plantilla actualizada exitosamente", {
        variant: "success",
      });
      navigate(`/app/solicitudes/${requestId}/receta/crear/buscar`);
    } catch (error) {
      console.error("Error updating template:", error);
      enqueueSnackbar("Error al actualizar la plantilla", {
        variant: "error",
      });
    }
  }, [
    templateName,
    templateDetails,
    templateId,
    updateTemplate,
    enqueueSnackbar,
    navigate,
    requestId,
  ]);

  if (isLoading) {
    return (
      <>
        <Header
          title={isMobile ? "Editar plantilla" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader title="Cargando plantilla..." />
          </Body>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      <Header
        title={isMobile ? "Editar plantilla" : undefined}
        useMekiDocIcon={!isMobile}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <ContentContainer disableGutters>
        <HeaderContainer>
          <Title variant="h4" fontWeight={"medium"}>
            Editar plantilla
          </Title>
          <RouterLink to={"buscar"}>
            <AddMedicineButton variant="outlined" size="large">
              <Typography variant="body1" fontWeight={"medium"}>
                Agregar medicamento
              </Typography>
            </AddMedicineButton>
          </RouterLink>
        </HeaderContainer>

        <Body>
          <PrescriptionContent>
            <StyledTextField
              label="Nombre de la plantilla"
              placeholder="Ej: Hipertensión arterial"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              fullWidth
            />

            {templateDetails.length > 0 ? (
              <>
                {heldDetails.length > 0 && (
                  <>
                    <SectionSubtitle>Receta retenida</SectionSubtitle>
                    <PrescriptionDetailsContainer>
                      {heldDetails.map((detail) => (
                        <PrescriptionDetailCard
                          key={detail.localId}
                          detail={detail}
                          handleEdit={() => handleEdit(detail)}
                          handleDelete={() => removeDetail(detail.localId)}
                        />
                      ))}
                    </PrescriptionDetailsContainer>
                  </>
                )}
                {simpleDetails.length > 0 && (
                  <>
                    <SectionSubtitle>Receta simple</SectionSubtitle>
                    <PrescriptionDetailsContainer>
                      {simpleDetails.map((detail) => (
                        <PrescriptionDetailCard
                          key={detail.localId}
                          detail={detail}
                          handleEdit={() => handleEdit(detail)}
                          handleDelete={() => removeDetail(detail.localId)}
                        />
                      ))}
                    </PrescriptionDetailsContainer>
                  </>
                )}
                <Footer>
                  <BottomActionArea
                    style={{ backgroundColor: "inherit", border: "none" }}
                  >
                    <BottomActionButton
                      style={{ width: "100%" }}
                      onClick={handleSave}
                      disabled={isSaving || !templateName.trim()}
                    >
                      {isSaving ? "Guardando..." : "Guardar plantilla"}
                    </BottomActionButton>
                  </BottomActionArea>
                </Footer>
              </>
            ) : (
              <EmptyRequest>
                <StyledInfoIconSecondary />
                No tienes medicamentos agregados
              </EmptyRequest>
            )}
          </PrescriptionContent>
        </Body>
      </ContentContainer>
    </>
  );
}
